/**
 * Acquisition + Activation tab.
 *
 * Pulls from two backend endpoints (per T2 PR-1 #1347):
 *   - GET /v1/admin/analytics/acquisition-activation?timeframe=...&channel=...
 *       → { timeframe, funnel, biggest_drop, channel_mix, cohort, geo }
 *   - GET /v1/admin/analytics/today-pulse
 *       → { signups_24h, signups_24h_prior, signups_wow_pct,
 *           match_ready_24h, activation_rate_24h }
 *
 * The "today pulse" card polls every 60s independently; the heavier
 * acquisition payload reloads only when the timeframe selector changes.
 *
 * UTM filter: spec calls out source/medium/campaign filters. T2 PR-1
 * exposes a single `channel` query param (the resolved first_channel),
 * not the raw UTM tuple — once metrics.attribution_by_channel exposes
 * per-UTM rows we'll surface the 3 dropdowns. For now we expose the
 * resolved `channel` filter via a free-text input that mirrors the
 * dropdown UX (one input, server-side resolution).
 *
 * Spec: docs/superpowers/specs/2026-05-28-admin-analytics-tabs-design.md §7.
 * Plan: docs/superpowers/plans/2026-05-28-admin-analytics-tabs.md Task 13.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApiUrl } from '@refinedev/core';
import { RefreshCw } from 'lucide-react';
import { adminFetch, AdminApiError } from '@/providers/admin-api';
import { TodayPulseCard } from './components/TodayPulseCard';
import { FunnelCard } from './components/FunnelCard';
import { ChannelMixCard } from './components/ChannelMixCard';
import { CohortTableCard } from './components/CohortTableCard';
import { GeoSplitCard } from './components/GeoSplitCard';

type Timeframe = '24h' | '7d' | '30d' | '90d';

interface AcquisitionResponse {
  timeframe: Timeframe;
  funnel: Array<{ stage: string; count: number }>;
  biggest_drop: { from: string; to: string; drop_pct: number };
  channel_mix: Array<{ first_channel: string | null; n: number }>;
  cohort: Array<{
    cohort_week: string;
    first_channel: string | null;
    activation_rate: string | number | null;
  }>;
  geo: Array<{ country: string | null; n: number }>;
}

interface PulseResponse {
  signups_24h: number;
  signups_24h_prior: number;
  signups_wow_pct: number | null;
  match_ready_24h: number;
  activation_rate_24h: number | null;
}

export function AcquisitionPage() {
  const apiUrl = useApiUrl();
  const [timeframe, setTimeframe] = useState<Timeframe>('7d');
  const [channel, setChannel] = useState<string>('');
  const [data, setData] = useState<AcquisitionResponse | null>(null);
  const [pulse, setPulse] = useState<PulseResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const qs = useMemo(() => {
    const params = new URLSearchParams({ timeframe });
    if (channel.trim()) params.set('channel', channel.trim());
    return params.toString();
  }, [timeframe, channel]);

  const loadAcquisition = useCallback(
    async (showSkeleton: boolean) => {
      if (showSkeleton) setLoading(true);
      else setRefreshing(true);
      setError(null);
      try {
        const res = await adminFetch(
          `${apiUrl}/v1/admin/analytics/acquisition-activation?${qs}`,
        );
        const d: AcquisitionResponse = await res.json();
        setData(d);
      } catch (e) {
        const msg =
          e instanceof AdminApiError
            ? `Failed to load acquisition (HTTP ${e.status}): ${e.message}`
            : (e as Error)?.message || 'Failed to load acquisition data';
        setError(msg);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [apiUrl, qs],
  );

  const loadPulse = useCallback(async () => {
    try {
      const res = await adminFetch(`${apiUrl}/v1/admin/analytics/today-pulse`);
      const p: PulseResponse = await res.json();
      setPulse(p);
    } catch {
      // Pulse failure is non-blocking — keep prior values; the bigger
      // payload's error banner already surfaces backend health issues.
    }
  }, [apiUrl]);

  useEffect(() => {
    void loadAcquisition(true);
  }, [loadAcquisition]);

  useEffect(() => {
    void loadPulse();
    const interval = setInterval(() => void loadPulse(), 60_000);
    return () => clearInterval(interval);
  }, [loadPulse]);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-bold">Acquisition + Activation</h2>
          <p className="text-muted-foreground">
            First-touch attribution, funnel performance, and cohort activation.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-muted-foreground" htmlFor="timeframe-select">
            Timeframe
          </label>
          <select
            id="timeframe-select"
            value={timeframe}
            onChange={(e) => setTimeframe(e.target.value as Timeframe)}
            className="rounded border bg-card px-2 py-1 text-sm"
          >
            <option value="24h">Last 24h</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
          <input
            type="text"
            value={channel}
            onChange={(e) => setChannel(e.target.value)}
            placeholder="Filter by channel…"
            className="rounded border bg-card px-2 py-1 text-sm w-40"
            aria-label="Channel filter"
          />
          <button
            onClick={() => void loadAcquisition(false)}
            disabled={refreshing}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
            aria-label="Refresh acquisition data"
          >
            <RefreshCw className={`h-3 w-3 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {pulse && <TodayPulseCard data={pulse} />}

      {error && (
        <div className="rounded-lg border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {loading && !data ? (
        <div className="rounded-lg border bg-card p-6 text-sm text-muted-foreground">
          Loading acquisition data…
        </div>
      ) : data ? (
        <>
          <FunnelCard data={data} />
          <ChannelMixCard data={data} />
          <CohortTableCard data={data} />
          <GeoSplitCard data={data} />
        </>
      ) : null}
    </div>
  );
}
